import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Box,
  Chip,
  CircularProgress,
  Tooltip,
  Badge,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AddCircleOutline,
  CloudUpload,
  FilterList,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { buildPermissionMap, hasRight } from 'state/checkPermission';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import DataGridComponent from 'components/DataGridComponent';
import ScrollToTop from 'components/ScrollToTop';
import ImportCrewDialog from './ImportCrewDialog';
import EmployeeFilterDialog, { EMPLOYEE_FILTER_EMPTY } from 'components/EmployeeFilterDialog';
import EmployeeCardList from './EmployeeCardList';

const TYPE_COLOR   = { CREW: 'primary', 'NON-CREW': 'default' };
const STATUS_COLOR = { true: 'success', false: 'error' };

const Employees = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const theme    = useTheme();
  const isXs     = useMediaQuery(theme.breakpoints.only('xs'));

  const user = useSelector((state) => state.user);
  const permissionMap = useMemo(() => buildPermissionMap(user?.permissions), [user?.permissions]);
  const canCreate = hasRight(permissionMap, 'employees', 'create', false);

  const scrollRef = useRef(null);

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPLOYEE_FILTER_EMPTY);
  const [filterOpen, setFilterOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);


  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/employees');
      setEmployees(Array.isArray(data) ? data : data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load employees.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  const filterOptions = useMemo(() => {
    const sets = { employeeTypes: new Set(), genders: new Set(), workingModes: new Set(),
                   ranks: new Set(), homebases: new Set(), departments: new Set(), divisions: new Set() };
    for (const e of employees) {
      if (e.employeeType) sets.employeeTypes.add(e.employeeType);
      if (e.gender)       sets.genders.add(e.gender);
      if (e.workingMode)  sets.workingModes.add(e.workingMode);
      if (e.rank)         sets.ranks.add(e.rank);
      if (e.homeBase)     sets.homebases.add(e.homeBase);
      if (e.department)   sets.departments.add(e.department);
      if (e.division)     sets.divisions.add(e.division);
    }
    return Object.fromEntries(Object.entries(sets).map(([k, s]) => [k, [...s].sort()]));
  }, [employees]);

  const activeFilterCount = useMemo(() => {
    const f = filters;
    return (f.employeeCode.trim()   ? 1 : 0) +
           (f.employeeName.trim()   ? 1 : 0) +
           (f.employeeTypes.length  > 0 ? 1 : 0) +
           (f.genders.length        > 0 ? 1 : 0) +
           (f.workingModes.length   > 0 ? 1 : 0) +
           (f.ranks.length          > 0 ? 1 : 0) +
           (f.homebases.length      > 0 ? 1 : 0) +
           (f.departments.length    > 0 ? 1 : 0) +
           (f.divisions.length      > 0 ? 1 : 0) +
           (f.isActive              ? 1 : 0);
  }, [filters]);

  const filtered = useMemo(() => {
    const f = filters;
    const codeQ = f.employeeCode.trim().toLowerCase();
    const nameQ = f.employeeName.trim().toLowerCase();
    const hasAny = codeQ || nameQ || f.employeeTypes.length || f.genders.length ||
      f.workingModes.length || f.ranks.length || f.homebases.length ||
      f.departments.length || f.divisions.length || f.isActive;

    let result = hasAny
      ? employees.filter(e => {
          if (codeQ                      && !e.employeeCode?.toLowerCase().includes(codeQ)) return false;
          if (nameQ                      && !e.employeeName?.toLowerCase().includes(nameQ)) return false;
          if (f.employeeTypes.length     && !f.employeeTypes.includes(e.employeeType))      return false;
          if (f.genders.length           && !f.genders.includes(e.gender))                  return false;
          if (f.workingModes.length      && !f.workingModes.includes(e.workingMode))        return false;
          if (f.ranks.length             && !f.ranks.includes(e.rank))                      return false;
          if (f.homebases.length         && !f.homebases.includes(e.homeBase))              return false;
          if (f.departments.length       && !f.departments.includes(e.department))          return false;
          if (f.divisions.length         && !f.divisions.includes(e.division))              return false;
          if (f.isActive === 'true'      && !e.isActive)                                    return false;
          if (f.isActive === 'false'     && e.isActive)                                     return false;
          return true;
        })
      : employees;

    const sorts = f.sorts?.length ? f.sorts : [];
    if (sorts.length) {
      result = [...result].sort((a, b) => {
        for (const { field, dir } of sorts) {
          const va = field === 'isActive' ? (a.isActive ? '1' : '0') : String(a[field] ?? '').toLowerCase();
          const vb = field === 'isActive' ? (b.isActive ? '1' : '0') : String(b[field] ?? '').toLowerCase();
          const cmp = dir === 'desc' ? vb.localeCompare(va) : va.localeCompare(vb);
          if (cmp !== 0) return cmp;
        }
        return 0;
      });
    }

    return result;
  }, [employees, filters]);

  const columns = useMemo(() => [
    {
      field: 'no', headerName: '#', width: 50, sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    { field: 'employeeCode', headerName: 'Code', width: 100 },
    { field: 'employeeName', headerName: 'Employee Name', flex: 2, minWidth: 160 },
    {
      field: 'employeeType', headerName: 'Type', width: 110,
      renderCell: ({ value }) => (
        <Chip label={value || 'NON-CREW'} color={TYPE_COLOR[value] || 'default'} size="small" />
      ),
    },
    { field: 'rank', headerName: 'Rank', width: 90 },
    { field: 'division', headerName: 'Division', flex: 1, minWidth: 120 },
    { field: 'department', headerName: 'Department', flex: 1, minWidth: 120 },
    { field: 'homeBase', headerName: 'Base', width: 80 },
    {
      field: 'isActive', headerName: 'Status', width: 90,
      renderCell: ({ value }) => (
        <Chip label={value ? 'Active' : 'Inactive'} color={STATUS_COLOR[value]} size="small" />
      ),
    },
  ], []);

  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header
          title="Employees"
          subtitle={`List of ${filtered.length} employee(s)`}
        />
        {!loading && (
          <Box display="flex" flexWrap="wrap" alignItems="center" gap={1}>
            {canCreate && (
              <Tooltip title="Import from Crew">
                <StyledButton onClick={() => setImportOpen(true)}>
                  <CloudUpload />
                </StyledButton>
              </Tooltip>
            )}
            {canCreate && (
              <Tooltip title="Create">
                <StyledButton onClick={() => navigate('/employees/new')}>
                  <AddCircleOutline />
                </StyledButton>
              </Tooltip>
            )}
            <Tooltip title="Filter">
              <StyledButton onClick={() => setFilterOpen(true)}>
                <Badge badgeContent={activeFilterCount || null} color="primary">
                  <FilterList />
                </Badge>
              </StyledButton>
            </Tooltip>
          </Box>
        )}
      </FlexBetween>

      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : (
        <Box
          ref={scrollRef}
          height={LAYOUT_HEIGHT}
          sx={{ overflowY: 'auto' }}
        >
          {isXs ? (
            <EmployeeCardList employees={filtered} />
          ) : (
            <DataGridComponent
              rows={filtered}
              columns={columns}
              loading={loading}
              props={{
                onRowClick: ({ row }) => navigate(`/employees/${row._id}`),
                sx: { cursor: 'pointer' },
                disableRowSelectionOnClick: true,
              }}
            />
          )}
          <ScrollToTop scrollRef={scrollRef} />
        </Box>
      )}

      <ImportCrewDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImportComplete={fetchEmployees}
      />

      <EmployeeFilterDialog
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onChange={setFilters}
        value={filters}
        options={filterOptions}
      />
    </Box>
  );
};

export default Employees;
